import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TimeTrackingProvider } from "./contexts/TimeTrackingContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ActivityProvider } from "./contexts/ActivityContext";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import LoginPage from "./pages/LoginPage";
import SetupPage from "./pages/SetupPage";
import DashboardPage from "./pages/DashboardPage";
import TasksPage from "./pages/TasksPage";
import TimePage from "./pages/TimePage";
import ProjectsPage from "./pages/ProjectsPage";
import CustomersPage from "./pages/CustomersPage";
import ChallengesPage from "./pages/ChallengesPage";
import BenefitsPage from "./pages/BenefitsPage";
import VacationPage from "./pages/VacationPage";
import AccessPage from "./pages/AccessPage";
import NotesPage from "./pages/NotesPage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import SystemPage from "./pages/SystemPage";
import ChatPage from "./pages/ChatPage";
import CustomerPortalPage from "./pages/CustomerPortalPage";
import AuditLogPage from "./pages/AuditLogPage";
import CalendarPage from "./pages/CalendarPage";
import HandbuchPage from "./pages/HandbuchPage";
import TeamPage from "./pages/TeamPage";
import FormsPage from "./pages/FormsPage";
import SocialFacesApplicationPage from "./pages/SocialFacesApplicationPage";
import ShootingZusagePage from "./pages/ShootingZusagePage";
import ShootingEinverstaendnisPage from "./pages/ShootingEinverstaendnisPage";
import ClientIntakePage from "./pages/ClientIntakePage";
import SocialFaceProfilePage from "./pages/SocialFaceProfilePage";
import SocialFacesAdminPage from "./pages/SocialFacesAdminPage";
import EinwilligungPage from "./pages/legal/EinwilligungPage";
import RecurringTasksPage from "./pages/RecurringTasksPage";
import MyActivityPage from "./pages/MyActivityPage";
import InfluencersPage from "./pages/InfluencersPage";
import CommunicationsPage from "./pages/CommunicationsPage";
import ContactsPage from "./pages/ContactsPage";
import SharedContactsPage from "./pages/SharedContactsPage";
import DatenschutzPage from "./pages/legal/DatenschutzPage";
import NutzungsbedingungenPage from "./pages/legal/NutzungsbedingungenPage";

// Layout
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// App Routes
const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Customer Portal - No Auth Required */}
            <Route path="/p/:customerId" element={<CustomerPortalPage />} />
            
            {/* Public Handbuch - No Auth Required */}
            <Route path="/handbuch" element={<HandbuchPage />} />
            
            {/* Public Shared Contacts - No Auth Required */}
            <Route path="/shared-contacts/:token" element={<SharedContactsPage />} />
            
            {/* Public Social Faces Application - No Auth Required */}
            <Route path="/social-faces" element={<SocialFacesApplicationPage />} />
            <Route path="/social-faces-de" element={<SocialFacesApplicationPage />} />
            
            {/* Public Shooting Confirmation - No Auth Required */}
            <Route path="/shooting-zusage" element={<ShootingZusagePage />} />
            
            {/* Public Shooting Einverständnis - No Auth Required */}
            <Route path="/einverstaendnis" element={<ShootingEinverstaendnisPage />} />
            
            {/* Public Client Intake Form - No Auth Required */}
            <Route path="/client-intake" element={<ClientIntakePage />} />

            {/* Public Social Face Selbstauskunft (Rechnungsstammdaten) - per Token-Link */}
            <Route path="/stammdaten/:token" element={<SocialFaceProfilePage />} />

            {/* Public Legal Pages - No Auth Required (für Google OAuth Consent) */}
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/einwilligung/:key" element={<EinwilligungPage />} />
            <Route path="/nutzungsbedingungen" element={<NutzungsbedingungenPage />} />
            
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route
                path="/"
                element={
                    <ProtectedRoute>
                        <TimeTrackingProvider>
                            <Layout />
                        </TimeTrackingProvider>
                    </ProtectedRoute>
                }
            >
                <Route index element={<DashboardPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="time" element={<TimePage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="challenges" element={<ChallengesPage />} />
                <Route path="benefits" element={<BenefitsPage />} />
                <Route path="vacation" element={<VacationPage />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="access" element={<AccessPage />} />
                <Route path="tools" element={<AccessPage />} />
                <Route path="passwords" element={<AccessPage />} />
                <Route path="notes" element={<NotesPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="profile" element={<SettingsPage />} />
                <Route path="system" element={<SystemPage />} />
                <Route path="audit-log" element={<AuditLogPage />} />
                <Route path="meine-aktivitaet" element={<MyActivityPage />} />
                <Route path="chat" element={<ChatPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="forms" element={<FormsPage />} />
                <Route path="influencers" element={<InfluencersPage />} />
                <Route path="communications" element={<CommunicationsPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="social-faces-admin" element={<SocialFacesAdminPage />} />
                <Route path="tasks/recurring" element={<RecurringTasksPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <ErrorBoundary>
            <ThemeProvider>
                <AuthProvider>
                    <ActivityProvider>
                        <NotificationProvider>
                            <BrowserRouter>
                                <ErrorBoundary>
                                    <AppRoutes />
                                </ErrorBoundary>
                                <Toaster position="top-right" richColors toastOptions={{ style: { marginTop: '72px' }, className: 'shadow-lg' }} />
                            </BrowserRouter>
                        </NotificationProvider>
                    </ActivityProvider>
                </AuthProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
